import { Link } from "react-router-dom";
import { PROBLEM_CODES, api, problemCode, useApiQuery } from "../api";
import { useWalletScope } from "../wallet";
import { Button, ButtonLink, EmptyState, SectionTitle } from "../components";
import { isZeroAmount } from "../format";
import { AsyncBoundary, PageHeader, ROUTES } from "../shell";
import { DashboardAnomalie } from "./DashboardAnomalie";
import { DashboardMissions, DashboardMissionsSquelette } from "./DashboardMissions";
import { MOIS_DEMANDES } from "./ProjectionConsommation";
import { DashboardProjection, DashboardProjectionSquelette } from "./DashboardProjection";
import { DashboardSeaux, DashboardSeauxSquelette } from "./DashboardSeaux";
import styles from "./Dashboard.module.css";

/** Pictogramme du bouton « Actualiser ». Décoratif, jamais annoncé. */
const ICONE_ACTUALISER = (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 12a8 8 0 1 1-2.34-5.66" />
    <path d="M20 4v4.5h-4.5" />
  </svg>
);

/** Pictogramme des états vides : un portefeuille fermé. */
const ICONE_PORTEFEUILLE = (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.5 7.5A2 2 0 0 1 5.5 5.5h11a2 2 0 0 1 2 2v1" />
    <rect x="3.5" y="7.5" width="17" height="11" rx="2" />
    <path d="M15.5 13h2" />
  </svg>
);

/**
 * Écran d'accueil : l'état du portefeuille d'un coup d'œil.
 *
 * Deux lectures indépendantes plutôt qu'une seule attente : les seaux
 * s'affichent dès qu'ils arrivent, sans attendre les missions. Un tableau de
 * missions lent ne doit pas retarder le chiffre qu'on vient consulter.
 *
 * Trois situations sont distinguées, parce qu'elles appellent trois gestes
 * différents — et c'est exactement la distinction que le back a pris soin de
 * faire en refusant une lecture sur un portefeuille inconnu (voir
 * `reporting.requireWallet`) :
 *
 * 1. **404 `wallet_not_found`** — aucune écriture n'existe pour cet
 *    identifiant. Le geste attendu est de corriger l'identifiant, ou de faire
 *    le premier achat. Ce n'est pas une panne : pas de trace d'erreur brute,
 *    un état vide qui le dit.
 * 2. **Portefeuille ouvert mais vide** — il existe au grand livre, ses quatre
 *    seaux sont à zéro. Le geste attendu est de créditer un pack.
 * 3. **Panne** — le service ne répond pas, ou répond mal. C'est
 *    `AsyncBoundary` qui s'en charge, avec son bouton « Réessayer ».
 */
export function DashboardPage() {
  // Le portefeuille vient de la portée courante — celui de la session pour une
  // entreprise, celui de l'entreprise consultée pour l'ESN. Il figure dans les
  // dépendances : un commercial qui passe d'un client à l'autre doit voir les
  // chiffres du second, pas ceux du premier.
  const { walletId, permissions, links } = useWalletScope();
  const portefeuille = useApiQuery((o) => api.getWalletState(walletId, o), [walletId]);
  const missions = useApiQuery((o) => api.listWalletMissions(walletId, o), [walletId]);
  const consommation = useApiQuery(
    (o) => api.getWalletConsumption(walletId, { months: MOIS_DEMANDES }, o),
    [walletId],
  );

  // `data === null` en plus du code : sur un rechargement raté, on garde les
  // chiffres déjà affichés plutôt que de les remplacer par un état vide.
  const introuvable =
    portefeuille.data === null &&
    problemCode(portefeuille.error) === PROBLEM_CODES.walletNotFound;

  // Un portefeuille qui existe mais dont rien n'est jamais entré. Le test porte
  // sur les deux témoins du grand livre, et jamais par une conversion en
  // `number` : ce sont des chaînes décimales, et `isZeroAmount` compare en
  // `BigInt`.
  const solde = portefeuille.data?.balance ?? null;
  const sansTokens =
    solde !== null && isZeroAmount(solde.in) && isZeroAmount(solde.total);

  const enLecture = portefeuille.loading || missions.loading || consommation.loading;

  function actualiser() {
    portefeuille.reload();
    missions.reload();
    consommation.reload();
  }

  return (
    <>
      <PageHeader
        eyebrow="Portefeuille"
        title="Dashboard"
        description="Ce qu'il vous reste à dépenser, où sont passés vos tokens, et vos projets en cours."
        actions={
          <>
            <Button
              variant="ghost"
              onClick={actualiser}
              loading={enLecture}
              iconLeft={ICONE_ACTUALISER}
            >
              Actualiser
            </Button>
            {/* L'action principale de l'écran : quand le solde baisse, c'est
                d'ici qu'on veut le remonter, sans chercher dans le menu. */}
            {links.achat !== null && (
              <ButtonLink variant="primary" to={links.achat}>
                Acheter des tokens
              </ButtonLink>
            )}
          </>
        }
      />

      {introuvable ? (
        <section className={styles.section} aria-labelledby="titre-seaux">
          <h2 id="titre-seaux" className="lde-visually-hidden">
            Soldes du portefeuille
          </h2>
          <EmptyState
            icon={ICONE_PORTEFEUILLE}
            title="Ce portefeuille n'existe pas encore."
            description={
              <>
                Aucune écriture n'a jamais été portée au grand livre pour le
                portefeuille <span className={styles.identifiant}>{walletId}</span>.
                {permissions.can_purchase
                  ? " Créditez un premier pack : c'est la première écriture qui ouvre le portefeuille."
                  : " Le client ouvrira son portefeuille en créditant un premier pack."}
              </>
            }
            action={
              permissions.can_purchase ? (
                <Link className={styles.lienAction} to={ROUTES.achat}>
                  Créditer un pack
                </Link>
              ) : undefined
            }
          />
        </section>
      ) : (
        <AsyncBoundary
          state={portefeuille}
          errorTitle="Soldes indisponibles"
          skeleton={<DashboardSeauxSquelette />}
          isEmpty={(etat) =>
            isZeroAmount(etat.balance.in) && isZeroAmount(etat.balance.total)
          }
          empty={
            <section className={styles.section} aria-labelledby="titre-seaux">
              <h2 id="titre-seaux" className="lde-visually-hidden">
                Soldes du portefeuille
              </h2>
              <EmptyState
                icon={ICONE_PORTEFEUILLE}
                title="Aucun token dans ce portefeuille."
                description="Le portefeuille existe bien au grand livre, mais rien n'y est entré : les quatre seaux sont à zéro. Un pack crédité l'alimentera."
                action={
                  permissions.can_purchase ? (
                    <Link className={styles.lienAction} to={ROUTES.achat}>
                      Créditer un pack
                    </Link>
                  ) : undefined
                }
              />
            </section>
          }
        >
          {(etat) => (
            <>
              {/* L'anomalie se place avant les chiffres et ne les remplace
                  jamais : elle avertit, elle ne masque pas. */}
              {etat.anomaly !== null && (
                <DashboardAnomalie anomaly={etat.anomaly} balance={etat.balance} />
              )}
              <DashboardSeaux balance={etat.balance} />
            </>
          )}
        </AsyncBoundary>
      )}

      {/* La projection se calcule sur la seule série des mouvements : le reste
          d'aujourd'hui y est reconstitué sur les mêmes écritures que les
          soldes, elle n'attend donc pas la balance. */}
      {!introuvable && !sansTokens && (
        <section className={styles.section} aria-labelledby="titre-projection">
          <SectionTitle id="titre-projection">
            Consommation
          </SectionTitle>
          <AsyncBoundary
            state={consommation}
            errorTitle="Consommation indisponible"
            skeleton={<DashboardProjectionSquelette />}
          >
            {(serie) => <DashboardProjection consommation={serie} />}
          </AsyncBoundary>
        </section>
      )}

      {/* Sans portefeuille — ou sans le moindre token — il n'y a pas de mission
          possible : afficher un second bloc vide n'apprendrait rien. */}
      {!introuvable && !sansTokens && (
        <section className={styles.section} aria-labelledby="titre-missions">
          <SectionTitle id="titre-missions">
            Projets
          </SectionTitle>
          <AsyncBoundary
            state={missions}
            errorTitle="Missions indisponibles"
            skeleton={<DashboardMissionsSquelette />}
          >
            {(liste) => <DashboardMissions missions={liste} />}
          </AsyncBoundary>
        </section>
      )}
    </>
  );
}
